import React, { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';

interface NavbarProps {
  activeSection: string;
}

const Navbar: React.FC<NavbarProps> = ({ activeSection }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const routerState = useRouterState();

  const scrollToSection = (sectionId: string): void => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleSectionClick = (event: React.MouseEvent, sectionId: string): void => {
    // If we're already on the home page, intercept to keep smooth scrolling
    if (routerState.location.pathname === '/') {
      event.preventDefault();
      scrollToSection(sectionId);
    } else {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-bg-dark/95 backdrop-blur-sm border-b border-border z-[1000] py-4">
      <div className="container flex justify-between items-center">
        <div className="flex flex-col font-orbitron">
          <span className="text-2xl font-black tracking-wider bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            knki
          </span>
          <span className="text-xs text-text-muted tracking-wide">KinkAsylum</span>
        </div>
        <ul
          className={`flex gap-6 list-none md:static md:flex-row md:bg-transparent md:border-0 md:shadow-none md:p-0 ${
            isMobileMenuOpen
              ? 'fixed left-0 top-[70px] flex-col bg-bg-dark w-full text-center transition-all duration-300 shadow-lg py-8 border-t border-border z-[999]'
              : 'fixed -left-full md:left-auto'
          }`}
        >
          <li>
            <Link
              to="/"
              hash="home"
              className={`text-text-secondary font-medium transition-colors relative ${
                activeSection === 'home' ? 'text-primary-light' : ''
              } hover:text-primary-light ${
                activeSection === 'home'
                  ? "after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:shadow-glow"
                  : ''
              }`}
              onClick={(e) => handleSectionClick(e, 'home')}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/events"
              className="text-text-secondary font-medium transition-colors hover:text-primary-light"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Events
            </Link>
          </li>
          <li>
            <Link
              to="/daily-tasks"
              className="text-text-secondary font-medium transition-colors hover:text-primary-light"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Daily Tasks
            </Link>
          </li>
          <li>
            <Link
              to="/"
              hash="about"
              className={`text-text-secondary font-medium transition-colors relative ${
                activeSection === 'about' ? 'text-primary-light' : ''
              } hover:text-primary-light ${
                activeSection === 'about'
                  ? "after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:shadow-glow"
                  : ''
              }`}
              onClick={(e) => handleSectionClick(e, 'about')}
            >
              About
            </Link>
          </li>
          <li>
            <Link
              to="/"
              hash="rules"
              className={`text-text-secondary font-medium transition-colors relative ${
                activeSection === 'rules' ? 'text-primary-light' : ''
              } hover:text-primary-light ${
                activeSection === 'rules'
                  ? "after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:shadow-glow"
                  : ''
              }`}
              onClick={(e) => handleSectionClick(e, 'rules')}
            >
              Rules
            </Link>
          </li>
          <li>
            <Link
              to="/"
              hash="stats"
              className={`text-text-secondary font-medium transition-colors relative ${
                activeSection === 'stats' ? 'text-primary-light' : ''
              } hover:text-primary-light ${
                activeSection === 'stats'
                  ? "after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:shadow-glow"
                  : ''
              }`}
              onClick={(e) => handleSectionClick(e, 'stats')}
            >
              Stats
            </Link>
          </li>
          <li>
            <Link
              to="/"
              hash="members"
              className={`text-text-secondary font-medium transition-colors relative ${
                activeSection === 'members' ? 'text-primary-light' : ''
              } hover:text-primary-light ${
                activeSection === 'members'
                  ? "after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:shadow-glow"
                  : ''
              }`}
              onClick={(e) => handleSectionClick(e, 'members')}
            >
              Members
            </Link>
          </li>
          <li>
            <Link
              to="/"
              hash="faq"
              className={`text-text-secondary font-medium transition-colors relative ${
                activeSection === 'faq' ? 'text-primary-light' : ''
              } hover:text-primary-light ${
                activeSection === 'faq'
                  ? "after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:shadow-glow"
                  : ''
              }`}
              onClick={(e) => handleSectionClick(e, 'faq')}
            >
              FAQ
            </Link>
          </li>
          <li>
            <Link
              to="/"
              hash="join"
              className={`text-text-secondary font-medium transition-colors relative ${
                activeSection === 'join' ? 'text-primary-light' : ''
              } hover:text-primary-light ${
                activeSection === 'join'
                  ? "after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:shadow-glow"
                  : ''
              }`}
              onClick={(e) => handleSectionClick(e, 'join')}
            >
              Join Us
            </Link>
          </li>
        </ul>
        <button
          className="md:hidden flex flex-col gap-1.5 bg-transparent border-0 cursor-pointer p-2 z-[1001]"
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <span
            className={`w-6 h-0.5 bg-text-primary transition-all duration-300 ${
              isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
            }`}
          ></span>
          <span
            className={`w-6 h-0.5 bg-text-primary transition-all duration-300 ${
              isMobileMenuOpen ? 'opacity-0' : ''
            }`}
          ></span>
          <span
            className={`w-6 h-0.5 bg-text-primary transition-all duration-300 ${
              isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
            }`}
          ></span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
